// src/app/components/newsletter/NewsletterSection.js
//
// Section newsletter de la page d'accueil (.nl-section de « 01 - home v5 »).
// Propre à l'accueil : elle reste dans le portal.
import React, { useId, useState } from "react";
import cls from "classnames";
import { useTranslation } from "react-i18next";
import { Countdown, Icon } from "@zorni/components-lib";

import useNewsletterSubscription from "./useNewsletterSubscription";
import { isValidEmail } from "./newsletterService";
import styles from "./NewsletterSection.module.css";

/* Informations éditoriales. null tant qu'elles ne sont pas réelles : le
   numéro et le compte à rebours sont alors masqués (pas de chiffres inventés).
   NEXT_ISSUE_AT : date ISO du prochain envoi. */
const ISSUE_NUMBER = null;
const NEXT_ISSUE_AT = null;

/* Aperçus de lettres, purement décoratifs. */
const PREVIEWS = [
  {
    date: "today",
    subjectStart: "Fès",
    subjectHighlight: "autrement",
    subjectEnd: " : la médina loin des foules",
    excerpt:
      "Au-delà des souks les plus fréquentés, des ateliers et des riads que peu de voyageurs connaissent…",
    img: "https://cdn.getyourguide.com/img/tour/5824f8b50d3f3.jpeg/145.jpg",
    tag: "Destinations",
    reading: "5 min",
  },
  {
    date: "week",
    subjectStart: "Agafay en",
    subjectHighlight: "slow travel",
    subjectEnd: " : une nuit sous les étoiles",
    excerpt: "À quarante minutes de Marrakech, le désert de pierre se vit au rythme du thé et du silence…",
  },
  {
    date: "twoWeeks",
    subjectStart: "Essaouira en",
    subjectHighlight: "hiver",
    subjectEnd: " : remparts, vent et lumière",
    excerpt: "Notre guide pour profiter de la cité des alizés quand les terrasses se vident…",
  },
];

export default function NewsletterSection() {
  const { t } = useTranslation();
  const { status, errorCode, subscribe, reset } = useNewsletterSubscription();
  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState(null);

  const inputId = useId();
  const messageId = useId();

  const dateLabel = {
    today: t("portal.home.newsletter.today", "Aujourd'hui"),
    week: t("portal.home.newsletter.weekAgo", "Il y a 7 jours"),
    twoWeeks: t("portal.home.newsletter.twoWeeksAgo", "Il y a 14 jours"),
  };

  const errorMessages = {
    invalid_email: t("portal.home.newsletter.errorInvalid", "Cette adresse email ne semble pas valide."),
    network: t("portal.home.newsletter.errorNetwork", "L'inscription n'a pas pu aboutir. Réessayez dans un instant."),
    unknown: t("portal.home.newsletter.errorNetwork", "L'inscription n'a pas pu aboutir. Réessayez dans un instant."),
  };

  const shownError = localError || (status === "error" ? errorCode : null);
  const isLoading = status === "loading";
  const isSuccess = status === "success";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;
    if (!isValidEmail(email)) {
      setLocalError("invalid_email");
      return;
    }
    setLocalError(null);
    const ok = await subscribe(email);
    if (ok) setEmail("");
  };

  const handleChange = (event) => {
    setEmail(event.target.value);
    if (localError) setLocalError(null);
    if (status === "error" || status === "success") reset();
  };

  return (
    <section className={styles.section} aria-labelledby="nl-title">
      <div className={styles.container}>
        {/* ------------------------------------------------ Aperçus */}
        <div className={styles.previews}>
          <header className={styles.header}>
            <p className={styles.kicker}>
              {t("portal.home.newsletter.kicker", "L'inspiration, dans votre boîte mail")}
            </p>
            <h2 id="nl-title" className={styles.headline}>
              {t("portal.home.newsletter.headlineStart", "Des récits de voyage,")}
              <em>{t("portal.home.newsletter.headlineHighlight", "pas du marketing.")}</em>
            </h2>
            <p className={styles.text}>
              {t(
                "portal.home.newsletter.text",
                "Une lettre rédigée par nos experts, pensée pour ceux qui voyagent avec intention. Destinations secrètes, rencontres authentiques, offres confidentielles."
              )}
            </p>
          </header>

          <div className={styles.stack} aria-hidden="true">
            {PREVIEWS.map((p, i) => (
              <article key={p.subjectStart} className={cls(styles.email, styles[`email${i + 1}`])}>
                <div className={styles.emailHeader}>
                  <span className={styles.emailBrand}>
                    <span className={styles.emailLogo}>
                      {/* Le monogramme de la marque, pas un globe générique :
                          ces aperçus imitent un e-mail reçu de Mondoviva. */}
                      <img src="/mondoviva-logo.png" alt="" />
                    </span>
                    <span className={styles.emailBrandName}>Mondoviva</span>
                  </span>
                  <span className={styles.emailDate}>{dateLabel[p.date]}</span>
                </div>
                {p.img && <div className={styles.emailImage} style={{ backgroundImage: `url("${p.img}")` }} />}
                <h3 className={styles.emailSubject}>
                  {p.subjectStart} <em>{p.subjectHighlight}</em>
                  {p.subjectEnd}
                </h3>
                <p className={styles.emailExcerpt}>{p.excerpt}</p>
                {p.tag && (
                  <div className={styles.emailMeta}>
                    <span className={styles.emailTag}>{p.tag}</span>
                    <span>
                      {p.reading} {t("portal.home.newsletter.reading", "de lecture")}
                    </span>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* ------------------------------------------------ Carte */}
        <div className={styles.card}>
          <div className={styles.cardContent}>
            {ISSUE_NUMBER && <div className={styles.issueNum}>N°{ISSUE_NUMBER}</div>}

            <h3 className={styles.cardTitle}>
              {t("portal.home.newsletter.cardTitleStart", "Recevez notre")}{" "}
              <em>{t("portal.home.newsletter.cardTitleHighlight", "prochaine lettre.")}</em>
            </h3>
            <p className={styles.cardDesc}>
              {t(
                "portal.home.newsletter.cardDesc",
                "Nos idées de voyage et nos offres privées, directement dans votre boîte mail. Désabonnement en un clic."
              )}
            </p>

            {NEXT_ISSUE_AT && (
              <Countdown
                endsAt={NEXT_ISSUE_AT}
                variant="strip"
                units={["days", "hours", "minutes"]}
                className={styles.countdown}
                ariaLabel={t("portal.home.newsletter.nextIssue", "Temps avant la prochaine lettre")}
                t={t}
              />
            )}

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <label htmlFor={inputId} className={styles.srOnly}>
                {t("portal.home.newsletter.emailLabel", "Adresse email")}
              </label>
              <div className={styles.inputWrap}>
                <input
                  id={inputId}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className={cls(styles.input, { [styles.inputInvalid]: shownError })}
                  placeholder={t("portal.home.newsletter.placeholder", "votre.email@exemple.com")}
                  value={email}
                  onChange={handleChange}
                  disabled={isLoading}
                  aria-invalid={shownError ? "true" : undefined}
                  aria-describedby={messageId}
                />
                <Icon name="iconEnvelope" size="md" color="inherit" />
              </div>

              <button
                type="submit"
                className={cls(styles.submit, { [styles.submitSuccess]: isSuccess })}
                disabled={isLoading}
                aria-busy={isLoading || undefined}
              >
                {isLoading && (
                  <>
                    <span className={styles.spinner} aria-hidden="true" />
                    {t("portal.home.newsletter.sending", "Inscription…")}
                  </>
                )}
                {isSuccess && (
                  <>
                    <Icon name="iconCheck" size="md" color="inherit" />
                    {t("portal.home.newsletter.success", "Merci, à bientôt !")}
                  </>
                )}
                {!isLoading && !isSuccess && (
                  <>
                    {t("portal.home.newsletter.submit", "S'abonner")}
                    <Icon name="iconArrowRight" size="md" color="inherit" />
                  </>
                )}
              </button>

              {/* Toujours présent : les lecteurs d'écran annoncent le
                  changement de contenu. */}
              <p
                id={messageId}
                className={cls(styles.message, {
                  [styles.messageError]: shownError,
                  [styles.messageSuccess]: isSuccess,
                })}
                role={shownError ? "alert" : "status"}
              >
                {shownError
                  ? errorMessages[shownError] || errorMessages.unknown
                  : isSuccess
                    ? t("portal.home.newsletter.successDetail", "Votre inscription est bien prise en compte.")
                    : ""}
              </p>
            </form>

            <ul className={styles.features}>
              <li className={styles.feat}>
                <span className={styles.featIcon}><Icon name="iconCheck" size="sm" color="inherit" /></span>
                <span>
                  <strong>{t("portal.home.newsletter.feat1Strong", "Offres")}</strong>{" "}
                  {t("portal.home.newsletter.feat1", "privées")}
                </span>
              </li>
              <li className={styles.feat}>
                <span className={styles.featIcon}><Icon name="iconCheck" size="sm" color="inherit" /></span>
                <span>
                  <strong>{t("portal.home.newsletter.feat2Strong", "Idées")}</strong>{" "}
                  {t("portal.home.newsletter.feat2", "de voyage")}
                </span>
              </li>
              <li className={styles.feat}>
                <span className={styles.featIcon}><Icon name="iconCheck" size="sm" color="inherit" /></span>
                <span>
                  <strong>{t("portal.home.newsletter.feat3Strong", "Désabonnement")}</strong>{" "}
                  {t("portal.home.newsletter.feat3", "en 1 clic")}
                </span>
              </li>
            </ul>

            <p className={styles.privacy}>
              <Icon name="iconShieldCheck" size="sm" color="inherit" />
              {t(
                "portal.home.newsletter.privacy",
                "Vos données sont traitées conformément à notre politique de confidentialité."
              )}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
